/*
 * Learning asset library
 *
 * These are intentionally not generic app icons. They are small, responsive
 * scenes that make every learning mode recognizable at a glance. SVG keeps
 * them crisp, themeable, and inexpensive enough to animate in a practice UI.
 */
import React, { useId } from 'react';

const Frame = ({ children, className = '', label, size = 44 }) => (
  <svg className={`lva ${className}`} width={size} height={size} viewBox="0 0 48 48" fill="none" role={label ? 'img' : undefined} aria-label={label} aria-hidden={label ? undefined : true} focusable="false">
    {children}
  </svg>
);

export const MemorySignal = ({ size = 48, label }) => (
  <Frame className="lva-memory" size={size} label={label}>
    <path className="lva-rail" d="M7 32c6-15 12 1 18-10S36 7 42 16" />
    <path className="lva-rail alt" d="M7 32c7 5 14 3 19-4s10-3 16 5" />
    <circle className="lva-node coral" cx="7" cy="32" r="4" />
    <circle className="lva-node gold" cx="25" cy="22" r="5.5" />
    <circle className="lva-node lime" cx="42" cy="16" r="4.5" />
    <circle className="lva-core" cx="25" cy="22" r="1.8" />
  </Frame>
);

const FIELD_SLOTS = [
  { x: 67, y: 63, labelX: 38, labelY: 35, anchor: 'start' },
  { x: 255, y: 68, labelX: 286, labelY: 42, anchor: 'end' },
  { x: 249, y: 185, labelX: 284, labelY: 215, anchor: 'end' },
  { x: 73, y: 188, labelX: 38, labelY: 216, anchor: 'start' },
];

const fieldTone = (status) => (['weak', 'fading', 'learning', 'strong'].includes(status) ? status : 'new');
const fieldName = (name, limit = 17) => (name?.length > limit ? `${name.slice(0, limit - 1)}…` : name || 'Untitled');
const fieldMastery = (mastery) => {
  const value = Number(mastery);
  return Number.isFinite(value) ? `${Math.round(Math.max(0, Math.min(1, value)) * 100)}%` : 'new';
};

/**
 * A data-linked memory scene for surfaces where a learner should see their
 * current map, rather than a decorative image. It intentionally has no
 * invented labels: every visible topic comes from the memory overview.
 */
export const MemoryField = ({ topics = [], score = null, label, className = '', size = 320 }) => {
  const uid = useId().replace(/:/g, '');
  const gridId = `memory-field-grid-${uid}`;
  const glowId = `memory-field-glow-${uid}`;
  const activeTopics = (topics || []).filter(Boolean).slice(0, 4).map((topic) => ({
    ...topic,
    tone: fieldTone(topic.status),
  }));
  const hasScore = score !== null && score !== undefined && Number.isFinite(Number(score));
  const scoreText = hasScore ? `${Math.round(Number(score))}%` : 'Start';
  const accessibleLabel = label || (activeTopics.length
    ? `Learning memory field showing ${activeTopics.length} active topics${hasScore ? ` and ${scoreText} held in memory` : ''}`
    : 'Learning memory field ready for the first practice signal');
  const route = (slot) => {
    const controlX = slot.x < 160 ? 124 : 196;
    const controlY = slot.y < 123 ? 86 : 168;
    return `M160 123 C${controlX} ${controlY}, ${slot.x} ${slot.y}, ${slot.x} ${slot.y}`;
  };

  return (
    <svg
      className={`lva lva-field ${className}`}
      width={size}
      height={Math.round(size * 0.76875)}
      viewBox="0 0 320 246"
      fill="none"
      role="img"
      aria-label={accessibleLabel}
      focusable="false"
    >
      <defs>
        <pattern id={gridId} width="18" height="18" patternUnits="userSpaceOnUse">
          <path d="M18 0H0V18" fill="none" stroke="rgba(255,255,255,.08)" strokeWidth=".6" />
          <circle cx=".5" cy=".5" r=".7" fill="rgba(255,255,255,.2)" />
        </pattern>
        <radialGradient id={glowId} cx="0" cy="0" r="1" gradientTransform="translate(160 123) rotate(90) scale(72)">
          <stop stopColor="rgba(209,238,147,.22)" />
          <stop offset=".55" stopColor="rgba(140,220,224,.08)" />
          <stop offset="1" stopColor="rgba(140,220,224,0)" />
        </radialGradient>
      </defs>

      <rect className="lva-field-surface" x=".5" y=".5" width="319" height="245" rx="18" />
      <rect className="lva-field-grid" x="10" y="10" width="300" height="226" rx="13" fill={`url(#${gridId})`} />
      <circle className="lva-field-glow" cx="160" cy="123" r="73" fill={`url(#${glowId})`} />
      <path className="lva-field-orbit outer" d="M102 123c0-32 26-58 58-58s58 26 58 58-26 58-58 58-58-26-58-58Z" />
      <path className="lva-field-orbit inner" d="M126 123c0-19 15-34 34-34s34 15 34 34-15 34-34 34-34-15-34-34Z" />

      {FIELD_SLOTS.map((slot, index) => {
        const topic = activeTopics[index];
        const tone = topic?.tone || 'ghost';
        return (
          <g key={`${slot.x}-${slot.y}`} className={`lva-field-point ${topic ? `is-active ${tone}` : 'is-ghost'}`} style={{ '--field-delay': `${index * 0.16}s` }}>
            <path className={`lva-field-route ${tone}`} d={route(slot)} />
            <circle className="lva-field-node-halo" cx={slot.x} cy={slot.y} r={topic ? 15 : 10} />
            <circle className="lva-field-node-dot" cx={slot.x} cy={slot.y} r={topic ? 6.6 : 3.2} />
            {topic && <>
              <circle className="lva-field-node-core" cx={slot.x} cy={slot.y} r="2.1" />
              <text className="lva-field-node-name" x={slot.labelX} y={slot.labelY} textAnchor={slot.anchor}>{fieldName(topic.name)}</text>
              <text className="lva-field-node-meta" x={slot.labelX} y={slot.labelY + 12} textAnchor={slot.anchor}>{fieldMastery(topic.mastery)}</text>
            </>}
          </g>
        );
      })}

      <g className="lva-field-core">
        <circle className="lva-field-core-halo" cx="160" cy="123" r="36" />
        <circle className="lva-field-core-ring" cx="160" cy="123" r="28" />
        <circle className="lva-field-core-body" cx="160" cy="123" r="21" />
        <text className="lva-field-core-value" x="160" y="120" textAnchor="middle">{scoreText}</text>
        <text className="lva-field-core-label" x="160" y="136" textAnchor="middle">{hasScore ? 'held' : 'your map'}</text>
      </g>
    </svg>
  );
};

export const HubShelf = ({ size = 44, label }) => (
  <Frame className="lva-hub" size={size} label={label}>
    <path className="lva-shelf" d="M7 13h34M7 25h34M7 37h34" />
    <path className="lva-book one" d="M10 10h7v12h-7z" />
    <path className="lva-book two" d="M20 8h8v16h-8z" />
    <path className="lva-book three" d="M31 11h6v12h-6z" />
    <circle className="lva-page-dot" cx="22" cy="17" r="1.4" />
    <path className="lva-book four" d="M12 28h11v7H12zM26 28h9v7h-9z" />
  </Frame>
);

export const TheoryBloom = ({ size = 44, label }) => (
  <Frame className="lva-theory" size={size} label={label}>
    <path className="lva-bloom-ring" d="M24 7v7M24 34v7M7 24h7M34 24h7M12 12l5 5M31 31l5 5M36 12l-5 5M17 31l-5 5" />
    <circle className="lva-bloom-core" cx="24" cy="24" r="8" />
    <path className="lva-bloom-line" d="M20 24h8M24 20v8" />
  </Frame>
);

export const QuizCompass = ({ size = 44, label }) => (
  <Frame className="lva-quiz" size={size} label={label}>
    <circle className="lva-compass-ring" cx="24" cy="24" r="15" />
    <path className="lva-compass-needle" d="m30 17-4 10-8 4 4-10z" />
    <circle className="lva-compass-dot" cx="24" cy="24" r="2" />
    <path className="lva-compass-ticks" d="M24 7v4M24 37v4M7 24h4M37 24h4" />
  </Frame>
);

export const FlashcardStack = ({ size = 44, label }) => (
  <Frame className="lva-cards" size={size} label={label}>
    <rect className="lva-card back" x="11" y="8" width="25" height="29" rx="3" />
    <rect className="lva-card mid" x="8" y="12" width="25" height="29" rx="3" />
    <rect className="lva-card front" x="14" y="15" width="25" height="25" rx="3" />
    <path className="lva-card-lines" d="M20 23h13M20 28h10M20 33h7" />
  </Frame>
);

export const CodeTrail = ({ size = 44, label }) => (
  <Frame className="lva-code" size={size} label={label}>
    <rect className="lva-code-window" x="7" y="9" width="34" height="29" rx="4" />
    <path className="lva-code-top" d="M7 16h34" />
    <circle className="lva-code-dot" cx="12" cy="12.5" r="1" />
    <path className="lva-code-lines" d="m15 24-4 3 4 3M23 24l4 3-4 3M30 23l-3 9" />
  </Frame>
);

export const InterviewStage = ({ size = 44, label }) => (
  <Frame className="lva-interview" size={size} label={label}>
    <rect className="lva-stage" x="7" y="9" width="34" height="23" rx="4" />
    <path className="lva-stage-stand" d="M24 32v7M17 40h14" />
    <circle className="lva-stage-person" cx="24" cy="19" r="4" />
    <path className="lva-stage-voice" d="M15 21a9 9 0 0 0 18 0M12 18a12 12 0 0 0 24 0" />
  </Frame>
);

export const RapidfireArc = ({ size = 44, label }) => (
  <Frame className="lva-rapid" size={size} label={label}>
    <path className="lva-rapid-arc back" d="M10 31a15 15 0 0 1 28-6" />
    <path className="lva-rapid-arc front" d="M10 31a15 15 0 0 0 24 6" />
    <path className="lva-rapid-bolt" d="m27 11-8 13h6l-3 13 9-15h-6z" />
    <circle className="lva-rapid-dot" cx="10" cy="31" r="2.5" />
  </Frame>
);

const SCENES = {
  theory: TheoryBloom,
  quiz: QuizCompass,
  flashcards: FlashcardStack,
  dsa: CodeTrail,
  interview: InterviewStage,
  rapidfire: RapidfireArc,
  hub: HubShelf,
  memory: MemorySignal,
};

export const LearningScene = ({ name, ...props }) => {
  const Scene = SCENES[name] || MemorySignal;
  return <Scene {...props} />;
};

export const LEARNING_ASSET_NAMES = Object.freeze(Object.keys(SCENES));
